/**
 * Locale-aware formatting — the single home for currency/number/date output.
 * Never build these with template literals or translation strings: formats
 * (digit grouping, currency symbols, month names, day order) differ per locale.
 *
 * `ar-LB` conventionally uses Western (Latin) digits, so it is pinned with
 * `-u-nu-latn`; drop the extension if Eastern Arabic digits are ever wanted.
 */
import i18n from '@/i18n';

const LOCALES: Record<string, string> = {
  en: 'en-US',
  ar: 'ar-LB-u-nu-latn',
  fr: 'fr-FR',
};

const locale = () => LOCALES[i18n.language] ?? 'en-US';

/** "$75" / "$12.50" — whole amounts drop the cents for cleaner price tags. */
export function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat(locale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Grouped integer/decimal: 12450 → "12,450" (en) / "12 450" (fr). */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat(locale()).format(n);
}

/** "Jun 27, 2026" by default; pass Intl options to reshape. */
export function formatDate(
  d: Date | string | number,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  return new Intl.DateTimeFormat(locale(), opts).format(new Date(d));
}

/** "7:30 PM" (en) / "19:30" (fr) — locale decides the clock. */
export function formatTime(d: Date | string | number): string {
  return new Intl.DateTimeFormat(locale(), { hour: 'numeric', minute: '2-digit' }).format(new Date(d));
}

/** "Sat, Jun 27 · 7:30 PM" — the match-card composite. */
export function formatDateTime(d: Date | string | number): string {
  const date = new Date(d);
  return `${formatDate(date, { weekday: 'short', day: 'numeric', month: 'short' })} · ${formatTime(date)}`;
}

/** 4200 → "4.2K" (en) / "4,2 k" (fr) — like counts, follower counts. */
export function formatCompact(n: number): string {
  return new Intl.NumberFormat(locale(), { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/** Localized month name (1-based): monthName(3) → "March" / "آذار"‎ / "mars". */
export function monthName(month: number): string {
  return new Intl.DateTimeFormat(locale(), { month: 'long' }).format(new Date(2000, month - 1, 1));
}

/** "2 hr. ago" / "منذ ساعتين" / "il y a 2 h" — feed/notification timestamps. */
export function formatRelativeTime(d: Date | string | number): string {
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'always', style: 'narrow' });
  const diffMs = new Date(d).getTime() - Date.now();
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
}
